// components/charts/LineChart.tsx
import React, { useMemo, useCallback } from "react";
import {
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

interface LineChartProps {
  title: string;
  description?: string;
  data: any[];
  xAxisKey: string;
  yAxisKey: string;
  lineColor?: string;
  showGrid?: boolean;
  height?: number;
  loading?: boolean;
  error?: string;
  strokeWidth?: number;
  showDots?: boolean;
  strokeDasharray?: string;
}

const LineChart: React.FC<LineChartProps> = ({
  title,
  description,
  data,
  xAxisKey,
  yAxisKey,
  lineColor = "#3b82f6",
  showGrid = true,
  height = 400,
  loading = false,
  error,
  strokeWidth = 2,
  showDots = true,
  strokeDasharray,
}) => {
  const CustomDot = useCallback(
    ({ cx, cy }: any) => (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill={lineColor}
        stroke="#fff"
        strokeWidth={2}
      />
    ),
    [lineColor]
  );

  const CustomActiveDot = useCallback(
    ({ cx, cy }: any) => (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={8}
          fill="transparent"
          stroke={lineColor}
          strokeWidth={2}
        />
        <circle cx={cx} cy={cy} r={4} fill={lineColor} />
      </g>
    ),
    [lineColor]
  );

  const CustomTooltip = useCallback(
    ({ active, payload, label }: any) => {
      if (!active || !payload?.length) return null;

      return (
        <div className="custom-tooltip">
          <p className="tooltip-title">{label}</p>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div
                className="tooltip-dot"
                style={{ backgroundColor: lineColor }}
              />
              <span>{payload[0].name || yAxisKey}</span>
            </div>
            <span className="tooltip-value">
              {typeof payload[0].value === "number"
                ? payload[0].value.toLocaleString()
                : payload[0].value}
            </span>
          </div>
        </div>
      );
    },
    [lineColor, yAxisKey]
  );

  const chartElements = useMemo(() => {
    if (!data?.length) return null;

    return (
      <>
        {showGrid && (
          <CartesianGrid
            strokeDasharray="2 4"
            stroke="#d1d5db"
            horizontal
            vertical
          />
        )}

        <XAxis
          dataKey={xAxisKey}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#6b7280" }}
        />

        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#6b7280" }}
        />

        <Tooltip
          content={<CustomTooltip />}
          cursor={{ stroke: "#d1d5db", strokeWidth: 1, strokeDasharray: "4 4" }}
        />

        <Line
          type="monotone"
          dataKey={yAxisKey}
          stroke={lineColor}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          dot={showDots ? <CustomDot /> : false}
          activeDot={showDots ? <CustomActiveDot /> : false}
        />
      </>
    );
  }, [
    data,
    xAxisKey,
    yAxisKey,
    lineColor,
    showGrid,
    strokeWidth,
    strokeDasharray,
    showDots,
    CustomDot,
    CustomActiveDot,
    CustomTooltip,
  ]);

  const chartStates = useMemo(
    () => ({
      loading: (
        <div className="chart-loading">
          <i className="ti ti-loader-2 spin" />
          <span>Loading...</span>
        </div>
      ),
      error: (
        <div className="chart-error">
          <i className="ti ti-alert-circle" />
          <span>{error}</span>
        </div>
      ),
      empty: (
        <div className="chart-empty">
          <i className="ti ti-chart-line" />
          <span>No data available</span>
        </div>
      ),
    }),
    [error]
  );

  const renderChart = () => {
    if (loading) return chartStates.loading;
    if (error) return chartStates.error;
    if (!data?.length) return chartStates.empty;

    return (
      <RechartsLineChart
        data={data}
        margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
        {chartElements}
      </RechartsLineChart>
    );
  };

  return (
    <div className="line-chart-container">
      <div className="chart-wrapper" style={{ height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LineChart;
